import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { resetLocationTimeValues, updateAddress, updateSameAddress } from './resLocationsSlice'
import { fetchInitContext } from './thunks'
import { computeTime } from './utils'
import type { RootState } from '..'
import type { ApprovaResContact, resFieldType, ResState } from './types'
import { setOrderDetailsDefaults } from './resDefaultsSlice'
import { OneWayDropDownOptions, OneWayOptions } from '@view/components/Reservations/locationAndTime/models'
import { setPoolFleetVehicles, setSelectedPoolFleetVehicle, setVehicleGroup } from './resVehicleSlice'
import { addBooker, updateBusiness } from './contextDropDownsSlice'
import { setOrderInputMandatoryFieldsValues, updateChargeableItems } from './resUiSlice'
import { setDate } from '@view/hooks/useOutOfHoursCheck'

/*
 * Want to keep these as the finally submitted form values for res edit
 */

export const initialState: ResState = {
	currentlyEditing: false,
	emailApprovalValue: 0,
	approvalContactId_all: null,
	approvalEmailAddress: null,
	reasonForHire: '',
	reasonForHireText: '',
	endDate: '',
	endTimeHours: '',
	endTimeMinutes: '',
	startDate: '',
	startTimeHours: '',
	startTimeMinutes: '',
	tariffBandId: '',
	insuranceType: '',
	collectionAddressId: '',
	collectionAddressType: '',
	collectionBranchCountryId: '',
	collectionBranchId: '',
	collectionLocationInformation: '',
	deliveryAddressId: '',
	deliveryAddressType: '',
	deliveryBranchId: '',
	deliveryLocationInformation: '',
	deliveryAddressBranchCountryId: '',
	approvalContactId_Customer: '',
	preSelectedPickUpType: '',
	oneWay: '',
	oneWayDropdownValue: '',
	autoAllocate: 0,
	deliveryFlightNumber: '',
	bespokeFields: {},
	deliveryAddress: null,
	collectionAddress: null,
	clientReference: '',
	isInsuranceExpired: false,
	resStartDateParsed: null,
	resEndDateParsed: null,
	totalDays: 0,
	chargeableItems: {},
	supplierInstructions: '',
	hiddenSupplierInstructions: null,
	additionToHiddenSupplierInstructions: '',
	contactId: '',
	businessId: '',
}

export const reservationEditFormSlice = createSlice({
	name: 'resEditForm',
	initialState,
	reducers: {
		resetLocationValues(state, action) {
			const type = action.payload

			state[`${type}Address`] = null

			if (!state.preSelectedPickUpType) {
				state.pickupType = ''
			}
		},
		updateFields(state, action) {
			const { name, value } = action.payload
			if (name === 'additionToHiddenSupplierInstructions') {
				state.additionToHiddenSupplierInstructions = value
				state.supplierInstructions = `${state.hiddenSupplierInstructions} ${value}`
			}
			state[name] = value
		},
		mergeOpeningHours(state, action) {
			const hours = action.payload
			const openingHours = { ...state.openingHours, ...hours }
			state.openingHours = openingHours
		},
		setOutOfHours(state, action) {
			state.outOfHoursHiddenValue = action.payload
		},
		setOpeningHours(state, action) {
			state.openingHours = action.payload
		},
		setTotalDays(state, action) {
			state.totalDays = action.payload
		},
		updateCurrentlyEditing(state, action) {
			if (action.payload === false) {
				return { ...initialState, contactId: state.contactId, businessId: state.businessId }
			} else {
				state.currentlyEditing = action.payload
			}
		},
		updateStopgapWarning(state, action) {
			state.acceptStopgapWarning = action.payload
		},
		updateChargeableItems(state, action) {
			for (const item of action.payload) {
				const { id, num } = item
				state[`chargeableItems[${id}]`] = Number(num)
			}
		},
		updateBespokeFields(state, action) {
			const { name, value } = action.payload

			state.bespokeFields = { ...state.bespokeFields, [name]: value }
		},
		updateResEditAddress(state, action) {
			const addressProps = { ...action.payload }
			return Object.assign(state, addressProps)
		},
		updateResApprovals(state, action) {
			const {
				contactIds,
				id,
				fieldType,
				fieldValue,
				listValue,
			}: {
				contactIds: ApprovaResContact[]
				id: string
				fieldType: resFieldType
				fieldValue: string
				listValue: boolean
			} = action.payload
			if (id && contactIds) {
				const approvalEmailAddress = listValue ? contactIds.find((c: ApprovaResContact) => c.id === id).emailAddress : id

				state.emailApprovalValue = 1
				state.approvalContactId_all = id
				state.approvalEmailAddress = approvalEmailAddress
				state[`approvalContactId_${fieldType}`] = id
				state[`approvalContactRequired_${fieldType}`] = 1
				state[`approvalValue_${fieldType}`] = fieldValue
			} else {
				state.emailApprovalValue = 0
			}
		},
		updateLocationInformation(state, action) {
			const { type, value }: { type: 'collection' | 'delivery'; value: string } = action.payload
			state[`${type}LocationInformation`] = value
		},
		updateInsuranceType(state, action) {
			const insuranceType = action.payload == 'Drivers' ? 'COI' : action.payload
			state.insuranceType = insuranceType
		},
		updateCostCentre(state, action) {
			state.costCentre = action.payload
		},

		updateReasonForHire(state, action) {
			const { text, value } = action.payload

			state.reasonForHire = value
			state.reasonForHireText = text
		},
		updateTarrifBandId(state, action) {
			state.tariffBandId = action.payload
		},

		updateStartDate(state, action) {
			if (action.payload?.StartDate) {
				const d = new Date(action.payload.StartDate)

				if (action.payload.StartTime) {
					const updatedDateTime = setDate(d, action.payload.StartTime)
					const updatedResStartDayParsed = Date.parse(updatedDateTime.toString())

					state.resStartDateParsed = updatedResStartDayParsed
				}

				const { day, hours, minutes } = computeTime(action.payload.StartDate, action.payload.StartTime)
				state.startDate = day
				state.startTimeHours = hours
				state.startTimeMinutes = minutes
			} else {
				state.startDate = ''
				state.totalDays = 0
			}
			delete state.rentalVehicleId
		},
		updateEndDate(state, action) {
			if (action.payload?.EndDate) {
				const d = new Date(action.payload.EndDate)

				if (action.payload.EndTime) {
					const updatedDateTime = setDate(d, action.payload.EndTime)
					const updatedResEndDayParsed = Date.parse(updatedDateTime.toString())

					state.resEndDateParsed = updatedResEndDayParsed
				}
				const { day, hours, minutes } = computeTime(action.payload.EndDate, action.payload.EndTime)
				state.endDate = day
				state.endTimeHours = hours
				state.endTimeMinutes = minutes
			} else {
				state.endDate = ''
				state.totalDays = 0
			}
			delete state.rentalVehicleId
		},
		updateResTimes(state, action: PayloadAction<{ name: 'startTime' | 'endTime'; time: string }>) {
			const { name, time = '' } = action.payload
			const [hours, minutes] = time.split(':')

			state[`${name}Hours`] = hours
			state[`${name}Minutes`] = minutes

			const startOrEnd = name.split('Time')[0] as 'start' | 'end'
			const d = new Date(state[`${startOrEnd}Date`].split('/').reverse().join('/'))
			const updatedDateTime = setDate(d, action.payload.time)
			const updatedResDayParsed = Date.parse(updatedDateTime.toString())
			const upperCasedStartOrEnd = `${startOrEnd.charAt(0).toUpperCase()}${startOrEnd.slice(1)}` as 'Start' | 'End'

			state[`res${upperCasedStartOrEnd}DateParsed`] = updatedResDayParsed

			delete state.rentalVehicleId
		},
		setOneWay(state, action: PayloadAction<OneWayDropDownOptions>) {
			const oneWayOptionsMapping = {
				No: '0',
				Yes: '1',
				Unsure: '1',
			}
			state.oneWayDropdownValue = action.payload
			state.oneWay = oneWayOptionsMapping[action.payload as keyof typeof oneWayOptionsMapping] as OneWayOptions
		},
		setPickUpType(state, action: PayloadAction<'APU' | 'TBA' | 'Return to Supplier Branch'>) {
			state.pickupType = action.payload
		},
		setPreSelectedPickUpType(state, action) {
			state.preSelectedPickUpType = action.payload

			if (!state.pickupType) {
				state.pickupType = action.payload
			}
		},
		setAutoAllocate(state, action: PayloadAction<number>) {
			state.autoAllocate = action.payload
		},
		setFlightNumber(state, action: PayloadAction<string>) {
			state.deliveryFlightNumber = action.payload
		},
		setIsInsuranceExpired(state, action) {
			state.isInsuranceExpired = action.payload
		},
		setRentalVehicleId(state, action) {
			state.rentalVehicleId = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(setPoolFleetVehicles, (state, action) => {
			if (!action.payload) {
				state.rentalVehicleId = ''
			}
		}),
			builder.addCase(setSelectedPoolFleetVehicle, (state, action) => {
				const { vehicleGroupId, vehicleId } = action.payload
				state.rentalVehicleId = vehicleId
				state.vehicleGroupId = vehicleGroupId
				state.insuranceType = 'COI'
			})
		builder.addCase(updateChargeableItems, (state, action) => {
			state.chargeableItems = {}
			if (action.payload.length > 0) {
				for (const { rates } of action.payload) {
					state.chargeableItems[rates.id] = rates.quantity.toString()
				}
			}
		}),
			builder.addCase(resetLocationTimeValues, state => {
				state.reasonForHire = ''
				state.reasonForHireText = ''
				state.endDate = ''
				state.endTimeHours = ''
				state.endTimeMinutes = ''
				state.startDate = ''
				state.startTimeHours = ''
				state.startTimeMinutes = ''
				state.tariffBandId = ''
				state.collectionAddressId = ''
				state.collectionAddressType = ''
				state.collectionBranchCountryId = ''
				state.collectionBranchId = ''
				state.collectionLocationInformation = ''
				state.deliveryAddressId = ''
				state.deliveryAddressType = ''
				state.deliveryBranchId = ''
				state.deliveryLocationInformation = ''
				state.deliveryAddressBranchCountryId = ''
				state.oneWay = ''
				state.deliveryFlightNumber = ''
				state.pickupType = ''
				state.resStartDateParsed = ''
				state.resEndDateParsed = ''
				state.totalDays = 0
				state.openingHours = null
				state.deliveryAddress = null
				state.collectionAddress = null
			}),
			builder.addCase(addBooker, (state, action) => {
				state.contactId = action.payload?.driverId
			})
		builder.addCase(setOrderInputMandatoryFieldsValues, (state, action) => {
			const { name, value } = action.payload

			state[name] = value
		}),
			builder.addCase(setVehicleGroup, (state, action) => {
				state.vehicleGroupId = action.payload.id

				if (action.payload.isTaxiVehicle) {
					state.insuranceType = 'COI'
				}
			})
		builder.addCase(updateBusiness, (state, action) => {
			state.businessId = action.payload.businessId
		}),
			builder.addCase(updateSameAddress, (state, action) => {
				const { addressType } = state.deliveryAddress
				if (action.payload) {
					state.collectionAddressType = 'Same'
					state.collectionAddressId = null
					state.collectionAddress = state.deliveryAddress
					state.collectionLocationInformation = state.deliveryAddress.text
					state.deliveryLocationInformation = state.deliveryAddress.text
					state.oneWay = 'No'

					if (addressType == 'Walk-In' || addressType == 'Airport') {
						state.pickupType = 'Return to Supplier Branch'
					}

					if (addressType == 'Business' || addressType == 'Home') {
						state.pickupType = state.preSelectedPickUpType
					}

					if (addressType == 'TBA') {
						state.pickupType = 'TBA'
					}
				} else {
					state.oneWay = ''
					state.collectionAddressId = ''
					state.collectionAddressType = null
					state.collectionAddress = null
					state.pickupType = state.preSelectedPickUpType
					state.collectionLocationInformation = ''
				}
				state.rentalVehicleId = ''
			})
		/* listen and grab country Id (all location searches will be with this id, so therefore the location ids will be same) */
		builder.addCase(fetchInitContext.fulfilled, (state, action) => {
			const { context } = action.payload
			if (context?.driver && context.permissions.reservationLimitedBooker) {
				const { id, name } = context.driver

				state.existingDriverTitleFormElement1 = ''
				state.existingDriverForenameFormElement1 = name.split(' ')[0]
				state.existingDriverSurnameFormElement1 = name.split(' ')[1]
				state.existingDriverIDFormElement1 = id
			}

			state.deliveryAddressBranchCountryId = context.countryId
			state.collectionBranchCountryId = context.countryId
			state.businessId = context.currentBusinessId
		})
		builder.addCase(updateAddress, (state, action) => {
			const { addressType, type, text } = action.payload
			state[`${type}LocationInformation`] = text

			if (type == 'collection') {
				if (addressType == 'Walk-In' || addressType == 'Airport') {
					state.pickupType = 'Return to Supplier Branch'
				}

				if (addressType == 'Business' || addressType == 'Home') {
					state.pickupType = state.preSelectedPickUpType
				}

				if (addressType == 'TBA') {
					state.pickupType = 'TBA'
				}
			}
			if (type == 'delivery') {
				if (addressType != 'Airport') {
					state.deliveryFlightNumber = ''
				}
			}
			state.rentalVehicleId = ''
		})
		builder.addCase(setOrderDetailsDefaults, (state, action) => {
			const { autoAllocate, insuranceType, orderNumberFormat, supplierInstructions, notes, hideSupplierInstructions } = action.payload

			state.clientReferencePrefix = orderNumberFormat || ''
			state.clientReference = `${orderNumberFormat || ''}${state.clientReference}`
			state.defaultAutoAllocate = autoAllocate ? 1 : 0
			state.autoAllocate = state.defaultAutoAllocate
			state.insuranceType =
				state.deliveryAddressType == 'International' || state.collectionAddressType == 'International' ? 'CDW' : insuranceType

			if (!state.supplierInstructions) {
				state.supplierInstructions = supplierInstructions
			}

			if (hideSupplierInstructions === 'hide') {
				state.hiddenSupplierInstructions = supplierInstructions
			}

			if (!state.customerInstructions) {
				state.customerInstructions = notes
			}
		})
	},
})
export const selectResEditForm = (state: RootState) => state.resEditForm

export const {
	updateCurrentlyEditing,
	updateResApprovals,
	updateReasonForHire,
	updateStopgapWarning,
	updateTarrifBandId,
	updateEndDate,
	updateStartDate,
	updateInsuranceType,
	updateCostCentre,
	updateLocationInformation,
	updateResEditAddress,
	updateBespokeFields,
	setOneWay,
	setPickUpType,
	setPreSelectedPickUpType,
	setAutoAllocate,
	setTotalDays,
	setOpeningHours,
	setOutOfHours,
	mergeOpeningHours,
	setFlightNumber,
	updateFields,
	updateResTimes,
	setIsInsuranceExpired,
	setRentalVehicleId,
	resetLocationValues,
} = reservationEditFormSlice.actions
export const resEditFormSliceReducer = reservationEditFormSlice.reducer
